import { useEffect, useMemo, useState } from "react";

import "./cartaoFidelidade.css";

const ROTS = [-8, 6, -5, 9, -10, 4, -6, 8, -4, 7];
const SEM_TEMA = {};
const CORES_CONFETE = ["#ffd04a", "#fff", "#ff6fa3", "#4fd1c5", "#f5b400", "#9b7bff"];

export const imagemUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//.test(url) || url.startsWith("/api/")) return url;
  return url.startsWith("/uploads/") ? `/api${url}` : url;
};

function Confete() {
  return (
    <div className="cf-confete">
      {Array.from({ length: 40 }, (_, k) => (
        <i key={k} style={{
          left: `${(k * 2.53) % 100}%`, top: `${(k * 7.9) % 100}%`, background: CORES_CONFETE[k % CORES_CONFETE.length],
          transform: `rotate(${(k * 47) % 360}deg)`, animationDuration: `${2.6 + (k % 6) * 0.45}s`, animationDelay: `-${(k * 0.61) % 3.5}s`,
        }} />
      ))}
    </div>
  );
}

/**
 * O cartão fidelidade do esboço do dono (23/10/2026), igual em todo lugar:
 * prévia do painel, acompanhamento do pedido, "Meu cartão" no cardápio,
 * Totem, Mesa QR e ficha do cliente. Os dados vêm da API (`fidelidade/cartao`):
 * `loja = {nome, logo, tema}` e `cartao = {carimbos, total, bonus, datas, premio}`.
 *
 * `carimboNovo` (posição 3–10): o cartão aparece um carimbo atrás e o novo
 * desce girando e "bate" — é a animação do acompanhamento. `animado={false}`
 * é a versão parada (a mesma da foto do WhatsApp).
 */
export default function CartaoFidelidade({ loja, cartao, animado = true, carimboNovo = null, rotulo = "Cartão fidelidade" }) {
  const tema = loja?.tema || SEM_TEMA;
  const total = cartao?.total || 10;
  const bonus = cartao?.bonus ?? 2;
  const final = Math.min(cartao?.carimbos ?? bonus, total);
  const [mostrados, setMostrados] = useState(carimboNovo ? final - 1 : final);
  useEffect(() => {
    if (!carimboNovo) { setMostrados(final); return undefined; }
    setMostrados(final - 1);
    const t = setTimeout(() => setMostrados(final), 700);
    return () => clearTimeout(t);
  }, [carimboNovo, final]);

  const estilo = useMemo(() => ({
    "--c1": tema.c1, "--c2": tema.c2, "--accent": tema.accent, "--paper": tema.paper, "--ink": tema.paper_ink,
  }), [tema]);
  const premio = cartao?.premio?.texto || "";
  const foto = imagemUrl(cartao?.premio?.foto);
  const completo = mostrados >= total;
  const bateu = carimboNovo && mostrados === final;
  const datas = cartao?.datas || [];

  const espacos = [];
  for (let i = 1; i <= total; i += 1) {
    const marcado = i <= mostrados;
    const novo = bateu && i === carimboNovo;
    if (marcado) {
      const etiqueta = i <= bonus
        ? <i className="cf-tag">BÔNUS</i>
        : (datas[i - bonus - 1] ? <i className="cf-data">{datas[i - bonus - 1]}</i> : null);
      const miolo = i === total
        ? (foto ? <><img src={foto} alt="" /><b className="cf-trofeu">🏆</b></> : <span>🏆</span>)
        : <span>{tema.emoji}</span>;
      espacos.push(<div key={i} className={`cf-slot marcado${i === total ? " final" : ""}${novo ? " novo" : ""}`}
        style={{ "--rot": `${ROTS[i - 1]}deg`, zIndex: i }}>{miolo}{etiqueta}</div>);
    } else if (i === total) {
      espacos.push(
        <div key={i} className="cf-slot premio" style={{ zIndex: i }}>
          <b className="cf-brilho">✨</b><b className="cf-brilho">✨</b><b className="cf-brilho">✨</b>
          {foto ? <img src={foto} alt="Prêmio" /> : <span>🎁</span>}
          <em className="cf-gratis">GRÁTIS</em>
        </div>,
      );
    } else {
      espacos.push(<div key={i} className="cf-slot" style={{ zIndex: i }}>{i}</div>);
    }
  }

  return (
    <div className="cf-wrap">
      <article className={`cf-card${animado ? " animado" : ""}${completo ? " completo" : ""}${bateu ? " batida" : ""}`}
        style={estilo} aria-label={`${rotulo}: ${mostrados} de ${total} carimbos`}>
        <div className="cf-deco">{(tema.deco || []).map((e, k) => <span key={k}>{e}</span>)}</div>
        {completo && <Confete />}
        {completo && <div className="cf-fita"><small>Parabéns!</small>{cartao?.status === "aguardando" ? "Cartão completo" : "Prêmio liberado"} 🎉</div>}
        <header className="cf-head">
          <div className="cf-brand">
            <div className="cf-logo">{loja?.logo ? <img src={imagemUrl(loja.logo)} alt="" /> : tema.emoji}</div>
            <div style={{ minWidth: 0 }}>
              <h3>{loja?.nome || "Minha loja"}</h3>
              <small>{rotulo}</small>
            </div>
          </div>
          <div className="cf-counter"><b className={bateu ? "sobe" : ""}>{mostrados}/{total}</b><span>carimbos</span></div>
        </header>
        <div className="cf-slots">{espacos}</div>
        <footer className="cf-foot">
          <small>{completo ? `Você completou os ${total} carimbos` : `Complete ${total} carimbos e ganhe`}</small>
          <b>{completo ? "🏆 " : "🎁 "}{premio}</b>
          <div className="cf-barra"><i style={{ width: `${(mostrados / total) * 100}%` }} /></div>
          <div className="cf-nucleo">Núcleo</div>
        </footer>
      </article>
    </div>
  );
}
